import * as React from "react";
import { cx } from "../internal/cx.js";

export type SystemMessageTone = "neutral" | "success" | "warning" | "danger";
export type SystemMessageDensity = "compact" | "comfortable";

export interface SystemMessageProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Leading visual — illustration, large icon, or emoji. */
  illustration?: React.ReactNode;
  /** Headline noun phrase ("No employees yet"). */
  title: React.ReactNode;
  /** Supporting copy that explains what happened and what to do next. */
  description?: React.ReactNode;
  /** Primary action (usually a Button). */
  primaryAction?: React.ReactNode;
  /** Optional secondary action (usually a "Learn more" link). */
  secondaryAction?: React.ReactNode;
  /** Tone — affects the illustration tint, not the message background. */
  tone?: SystemMessageTone;
  /** Compact density removes the bordered card chrome (for in-table use). */
  density?: SystemMessageDensity;
}

/**
 * Empty / alt-state banner with optional illustration. Different from `<Alert>`:
 * SystemMessage *replaces* content (the table has no rows, the search returned
 * nothing, the user is unauthorized), whereas Alert *annotates* content that
 * is still there. Larger and centered so it works as a stand-in for a missing
 * dataset.
 */
export const SystemMessage = React.forwardRef<HTMLDivElement, SystemMessageProps>(
  function SystemMessage(
    {
      className,
      density = "comfortable",
      description,
      illustration,
      primaryAction,
      secondaryAction,
      title,
      tone = "neutral",
      ...props
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        // biome-ignore lint/a11y/useSemanticElements: <output role="status"> is a form-output element coupled to name=/for=; a div with role=status is the standard live-region pattern for system messages outside of a form.
        role="status"
        className={cx(
          "cw-v2-system-message",
          `cw-v2-system-message--${tone}`,
          `cw-v2-system-message--${density}`,
          className,
        )}
        {...props}
      >
        {illustration ? (
          <div aria-hidden className="cw-v2-system-message__illustration">
            {illustration}
          </div>
        ) : null}
        <div className="cw-v2-system-message__body">
          <div className="cw-v2-system-message__title">{title}</div>
          {description ? (
            <div className="cw-v2-system-message__description">{description}</div>
          ) : null}
        </div>
        {primaryAction || secondaryAction ? (
          <div className="cw-v2-system-message__actions">
            {primaryAction}
            {secondaryAction}
          </div>
        ) : null}
      </div>
    );
  },
);
SystemMessage.displayName = "SystemMessage";
